import { $bitrixUserContext, addFavoritesProduct } from '@context/useBitrixUser/useBitrix';
import {
  ServiceData,
  SetUpServiceData,
} from '@pages/AdditionalServices/containers/methods/basket/types';
import { SEARCH_PARAMS } from '@pages/consts';
import { orderService } from '@services/requests/orderService';
import { Order } from '@services/requests/orderService/interface';
import { OrderProduct } from '@services/requests/orderService/orderProductsLoader/interface';
import { RouteResult } from '@services/requests/orderService/types';
import { useStateSubscriber } from '@settings/core/stateContexts/useContextSubscriber';
import { $error } from '@settings/errorContext';
import { BehaviorSubject } from 'rxjs';
import { getValueFromSearchParamsByKey } from 'src/utils/search-params';

import {
  checkCartExpireDate,
  getSavedCartState,
  getSavedCartStateByUserId,
  removeSavedCartStateByUserId,
  setSavedCartState,
} from './cart-helpers';
import BASKET_CONSTANTS from './consts';
import { OrderState } from './interfaces';

export const LOCALSTORAGE_CART_KEY = '__ON-LOG_CART__';
const $orderId = new BehaviorSubject<string | undefined>(undefined);
const $order = new BehaviorSubject<OrderState>(BASKET_CONSTANTS.order);
const $productsIds = new BehaviorSubject<string[]>([]);

/**
 * getOrderPipe возвращает subscriber для возможности подписки
 * на изменение заказа и других данных.
 */
export const getOrderPipe = () => {
  return $order.pipe();
};

/**
 * resetEditProductContext сбрасывает состояние редактируемого продукта корзины.
 */
const resetEditProductContext = () => {
  $order.next({ ...$order.getValue(), editProduct: null });
};

/**
 * Инициализация
 */
const init = async (language_id: string, currency_id: string) => {
  checkCartExpireDate();
  const savedCartState = getSavedCartState();
  const foundCartByUserId = getSavedCartStateByUserId(savedCartState);

  if (!foundCartByUserId) {
    const order = $order.getValue();

    $order.next({
      ...order,
      language_id,
      currency_id,
      isLoading: false,
    });
    return;
  }

  const { orderId } = foundCartByUserId;

  const editProductId = getValueFromSearchParamsByKey(SEARCH_PARAMS.editProductId);

  try {
    const order = await orderService('').Load('preOrder', orderId);

    const foundEditProduct = order.products.find((product) => product.id === editProductId);

    $order.next({
      ...order,
      editProduct: foundEditProduct,
      isLoading: false,
    });
    $orderId.next(orderId);
  } catch (e) {
    removeSavedCartStateByUserId(savedCartState);
    $error.next(e);
  } finally {
    $order.next({
      ...$order.getValue(),
      isLoading: false,
    });
  }
};

/**
 * Инициализация корзины по подписке на пользователя,
 * когда пользователь загрузится
 */
const initBasket = (language_id: string, currency_id: string) => {
  $bitrixUserContext.subscribe(({ isUserLoading }) => {
    if (isUserLoading) {
      return;
    }

    init(language_id, currency_id);
  });
};

/**
 * Обработчик оформления заказа
 * @param id
 * @param name
 * @param email
 * @param phone
 */

const completeOrder = async (id: string, comment: string) => {
  const order = $order.getValue();
  const products = $productsIds.getValue();

  const selectedProducts = order.products.filter((p) => products.includes(p.id));

  const result = await orderService('').SaveOrder({
    ...order,
    products: selectedProducts,
    id: id,
    pre_order_id: order.id,
    customer_id: id,
    comment,
  });

  reInitCart();

  return result;
};

/**
 * Сброс состояния корзины
 */
const reInitCart = async () => {
  const order = $order.getValue();
  const products = $productsIds.getValue();

  const restProducts = order.products.filter((p) => !products.includes(p.id));
  const isRestProductsEmpty = restProducts.length === 0;

  if (isRestProductsEmpty) {
    $orderId.next(undefined);
    $order.next(BASKET_CONSTANTS.order);
    $productsIds.next(BASKET_CONSTANTS.order.products.map((p) => p.id));

    const savedCartState = getSavedCartState();
    removeSavedCartStateByUserId(savedCartState);
    return;
  }

  const { id: _, ...restOrder } = order;
  const newTempOrder = { ...restOrder, products: restProducts, id: '' };

  const newOrder = await saveCart(newTempOrder);

  $orderId.next(newOrder.id);
  $order.next(newOrder);
  $productsIds.next(newOrder.products.map((p) => p.id));
};

/**
 * Обработчик сохранения корзины в localStorage
 */
const saveCart = async (order: Order): Promise<OrderState> => {
  const newOrder = await orderService('').SavePreOrder(order);
  setSavedCartState(newOrder.id);

  $orderId.next(newOrder.id);

  return {
    ...$order.getValue(),
    ...newOrder,
    products: newOrder?.pre_order_products || [],
    isLoading: false,
    pre_order_products: undefined,
  };
};

/**
 * Сравнение двух продуктов в зависимости от их типов
 * @param a
 * @param b
 */
export function isProductEquals(a: OrderProduct, b: OrderProduct) {
  const { product_type, data } = a;

  if (b.product_type !== product_type) {
    return false;
  }

  switch (product_type) {
    case 'route':
      const newRoute: RouteResult = JSON.parse(b.data);
      const currentRoute: RouteResult = JSON.parse(data);

      const isRouteGroupsEquals = newRoute.route.routes
        .map((r) => r.routeId)
        .map((id) => currentRoute.route.routes.map((r) => r.routeId).includes(id))
        .reduce((res: boolean, val: boolean): boolean => res && val, true);
      return (
        isRouteGroupsEquals && newRoute.route.routes.length === currentRoute.route.routes.length
      );
    default:
      return false;
  }
}

/**
 * Обработчик добавления товара в корзину
 * @param order
 * @param product
 * @param replaceProductId
 */
const addProductToCart = (
  order: Order,
  product: OrderProduct,
  replaceProductId?: string
): Order => {
  const existProduct = order.products.find((p) => isProductEquals(p, product));
  const foundReplceableProductById = order.products.find(
    (product) => product.id === replaceProductId
  );

  if (!existProduct && !foundReplceableProductById) {
    return {
      ...order,
      products: [...order.products, product],
    };
  }

  // Если переданный заменяемый id продукта передан и найден, то вместо
  // добавления производится замена продукта на новый
  if (foundReplceableProductById) {
    const replaceProductIndex = order.products.findIndex((p) => p.id === replaceProductId);
    const newProducts = [...order.products];

    newProducts.splice(replaceProductIndex, 1, product);

    return {
      ...order,
      products: [...newProducts],
    };
  }

  return {
    ...order,
    products: order.products.map((p) => {
      if (isProductEquals(p, product)) {
        p.amount += product.amount;
      }

      return p;
    }),
  };
};

const createServiceTempPreOrder = async (services: (ServiceData | SetUpServiceData)[]) => {
  if (0 === services.length) {
    return;
  }

  const setUpService: SetUpServiceData = services.find(
    (s: SetUpServiceData) => s?.IsSetupService
  ) as SetUpServiceData;
  if (!setUpService) {
    return;
  }

  try {
    const serviceGroup = await orderService('').SavePreOrderGroup(
      services.map((s) => {
        const price = s.service.price_offer_result.result.fullPriceInTargetCurrency;
        return {
          amount: 1,
          price: price === -1 ? 0 : price,
          product_type: 'service',
          token: s.service.price_offer_result.token,
          data: JSON.stringify({
            ...s,
            sectionName: setUpService.ServicesPath['0'],
            serviceParent: setUpService.Variant,
            dateAddedToCart: new Date().toISOString(),
          }),
        };
      })
    );

    const { currency_id, language_id } = $order.getValue();

    const newPreOrder = await orderService('').SavePreOrder({
      ...BASKET_CONSTANTS.order,
      currency_id,
      language_id,
      id: '',
      products: [serviceGroup],
    });

    return {
      ...newPreOrder,
      products: newPreOrder!.pre_order_products || [],
    };
  } catch (e) {
    console.error(e);
  }
};

/**
 * Обработчик добавления в корзину
 * @param product
 * @param userId
 */
const addToCart = async (product: OrderProduct, replaceProductId?: string) => {
  const order = $order.getValue();

  $order.next(
    await saveCart(addProductToCart(JSON.parse(JSON.stringify(order)), product, replaceProductId))
  );
};

/**
 * addToFavoritesGroupOfServices принимает на вход предварительно подготовленные
 * данные услуг, формирует из них единый продукт для избранного и добавляет его.
 * @param services
 */
const addToFavoritesGroupOfServices = async (services: (ServiceData | SetUpServiceData)[]) => {
  if (0 === services.length) {
    return;
  }

  const setUpService: SetUpServiceData = services.find(
    (s: SetUpServiceData) => s?.IsSetupService
  ) as SetUpServiceData;
  if (!setUpService) {
    return;
  }

  try {
    const serviceGroup = await orderService('').SavePreOrderGroup(
      services.map((s) => {
        const price = s.service.price_offer_result.result.fullPriceInTargetCurrency;
        return {
          amount: 1,
          price: price === -1 ? 0 : price,
          product_type: 'service',
          token: s.service.price_offer_result.token,
          data: JSON.stringify({
            ...s,
            sectionName: setUpService.ServicesPath['0'],
            serviceParent: setUpService.Variant,
            dateAddedToCart: new Date().toISOString(),
          }),
        };
      })
    );

    await addFavoritesProduct({ type: 'complexProduct', data: JSON.stringify(serviceGroup) });
  } catch (e) {
    console.error(e);
  }
};

/**
 * addToCartGroupOfServices принимает на вход предварительно подготовленные
 * данные услуг и формирует из них единый продукт для заказа.
 * @param services
 * @param userId
 */
const addToCartGroupOfServices = async (
  services: (ServiceData | SetUpServiceData)[],
  replaceProductId?: string
) => {
  if (0 === services.length) {
    return;
  }

  const setUpService: SetUpServiceData = services.find(
    (s: SetUpServiceData) => s?.IsSetupService
  ) as SetUpServiceData;
  if (!setUpService) {
    return;
  }

  try {
    const serviceGroup = await orderService('').SavePreOrderGroup(
      services.map((s) => {
        const price = s.service.price_offer_result.result.fullPriceInTargetCurrency;
        return {
          amount: 1,
          price: price === -1 ? 0 : price,
          product_type: 'service',
          token: s.service.price_offer_result.token,
          data: JSON.stringify({
            ...s,
            sectionName: setUpService.ServicesPath['0'],
            serviceParent: setUpService.Variant,
            dateAddedToCart: new Date().toISOString(),
          }),
        };
      })
    );

    return await addToCart(serviceGroup, replaceProductId);
  } catch (e) {
    console.error(e);
  }
};

/**
 * addServiceToFavorites добавляет переданную услугу в избранное как отдельный товар
 * @param service
 */
const addServiceToFavorites = async (service: SetUpServiceData) => {
  try {
    const price = service.service.price_offer_result.result.fullPriceInTargetCurrency;

    const { currency_id, language_id } = $order.getValue();

    const order = await orderService('').SavePreOrder({
      ...BASKET_CONSTANTS.order,
      currency_id,
      language_id,
      id: '',
      products: [
        {
          amount: 1,
          data: JSON.stringify({
            ...service,
            sectionName: service.ServicesPath['0'],
            serviceParent: service.Variant,
            dateAddedToCart: new Date().toISOString(),
          }),
          price: price === -1 ? 0 : price,
          product_type: 'service',
          token: service.service.price_offer_result.token,
        },
      ],
    });

    const orderProduct = order.pre_order_products?.[0];

    if (!orderProduct) {
      return;
    }

    await addFavoritesProduct({
      type: 'service',
      data: JSON.stringify({
        id: orderProduct.id,
        price: orderProduct.price,
        product_type: orderProduct.product_type,
        token: orderProduct.token,
        amount: orderProduct.amount,
        data: orderProduct.data,
      }),
    });
  } catch (e) {
    console.error(e);
  }
};

/**
 * addServiceToCart добавляет переданную услугу в корзину как отдельный товар
 * @param service
 */
const addServiceToCart = async (service: SetUpServiceData) => {
  try {
    const price = service.service.price_offer_result.result.fullPriceInTargetCurrency;
    await addToCart({
      id: service.ServiceData.id,
      amount: 1,
      data: JSON.stringify({
        ...service,
        sectionName: service.ServicesPath['0'],
        serviceParent: service.Variant,
        dateAddedToCart: new Date().toISOString(),
      }),
      price: price === -1 ? 0 : price,
      product_type: 'service',
      token: service.service.price_offer_result.token,
    });
  } catch (e) {
    console.error(e);
  }
};

/**
 * Обработчик удаления продукта из корзины
 * @param product
 * @param userId
 */
const deleteFromCart = async (product: OrderProduct) => {
  const savedCartState = getSavedCartState();
  const order = $order.getValue();
  const productsIds = $productsIds.getValue();

  const tmpOrder = {
    ...JSON.parse(JSON.stringify(order)),
    products: order.products.filter((p) => p.id !== product.id),
  };

  $productsIds.next(productsIds.filter((id) => id !== product.id));
  $order.next(await saveCart(tmpOrder));

  if (!tmpOrder.products.length) {
    removeSavedCartStateByUserId(savedCartState);
  }
};

/**
 * Обработчик очистки корзины
 */
const clearCart = async () => {
  const savedCartState = getSavedCartState();
  const order = $order.getValue();

  const tmpOrder = {
    ...JSON.parse(JSON.stringify(order)),
    products: [],
  };
  $productsIds.next([]);
  $order.next(await saveCart(tmpOrder));

  if (!tmpOrder.products.length) {
    removeSavedCartStateByUserId(savedCartState);
  }
};

/**
 * Обработчик создания временного предзаказа для возможности
 * формирования КП в pdf
 */
const createTempGroupPreOrder = async (products: OrderProduct[]): Promise<Order> => {
  const { currency_id, language_id } = $order.getValue();

  const newPreOrder = await orderService('').SavePreOrder({
    ...BASKET_CONSTANTS.order,
    currency_id,
    language_id,
    id: '',
    products: products,
  });

  return {
    ...newPreOrder,
    products: newPreOrder!.pre_order_products || [],
  };
};

/**
 * Обработчик создания временного предзаказа для возможности
 * формирования КП в pdf
 */
const createTempPreOrder = async (product: OrderProduct): Promise<Order> => {
  const { currency_id, language_id } = $order.getValue();
  const newPreOrder = await orderService('').SavePreOrder({
    ...BASKET_CONSTANTS.order,
    currency_id,
    language_id,
    id: '',
    products: [product],
  });

  $productsIds.next(newPreOrder!.pre_order_products.map((p) => p.id) || []);

  return {
    ...newPreOrder,
    products: newPreOrder!.pre_order_products || [],
  };
};

const actions = {
  completeOrder,
  reInitCart,
  saveCart,
  addProductToCart,
  addToCart,
  deleteFromCart,
  clearCart,
  addToCartGroupOfServices,
  addServiceToCart,
  init: initBasket,
  createTempPreOrder,
  createServiceTempPreOrder,
  createTempGroupPreOrder,
  addToFavoritesGroupOfServices,
  addServiceToFavorites,
};

const useBasket = () => {
  const useOrderId = () => useStateSubscriber<string | undefined>($orderId);
  const useOrder = () => useStateSubscriber<OrderState>($order);
  const useProductsIds = () => useStateSubscriber<string[]>($productsIds);

  return {
    useOrderId,
    useOrder,
    useProductsIds,
    actions,
  };
};

export default useBasket;
